import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, Plus, Trash2, Bot, Zap, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import { adminWorklogList, adminWorklogAdd, adminWorklogUpdate, adminWorklogDelete } from "@/lib/api";

const MODES = ["Trusted Automation", "Approval Required", "Recommend Only"];
const STATUSES = ["done", "pending", "approved"];
const input = "w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-brand outline-none";
const label = "text-xs font-semibold text-slate-600 block mb-1";

const MODE_TONE = {
  "Trusted Automation": "bg-emerald-50 text-emerald-700",
  "Approval Required": "bg-amber-50 text-amber-700",
  "Recommend Only": "bg-blue-50 text-brand",
};
const STATUS_TONE = { done: "bg-emerald-50 text-emerald-700", pending: "bg-amber-50 text-amber-700", approved: "bg-blue-50 text-brand" };
const BLANK = { action: "", reason: "", pages: "", mode: "Approval Required", status: "pending", before: "", after: "", rollback: false };

export default function AdminWorklog() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [adding, setAdding] = useState(false);

  const load = () => adminWorklogList().then((d) => setItems(d.items || [])).catch(() => toast.error("Failed to load — admin login required"));
  useEffect(() => { load(); }, []);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const add = async () => {
    if (!form.action.trim()) { toast.error("Describe the action"); return; }
    setAdding(true);
    try { await adminWorklogAdd({ ...form, action: form.action.trim() }); setForm(BLANK); toast.success("Logged — live on /growth-agent"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
    finally { setAdding(false); }
  };
  const cycleStatus = async (it) => {
    const next = STATUSES[(STATUSES.indexOf(it.status) + 1) % STATUSES.length];
    try { await adminWorklogUpdate(it.id, { status: next }); load(); } catch { toast.error("Update failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this work log entry?")) return;
    try { await adminWorklogDelete(id); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); }
  };

  if (!items) return <main className="min-h-screen bg-slate-50 grid place-items-center"><Loader2 className="animate-spin text-brand" size={26} /></main>;

  const autoCount = items.filter((i) => i.auto).length;
  return (
    <main className="min-h-screen bg-slate-50 pb-24" data-testid="admin-worklog">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/admin" className="text-slate-500 hover:text-brand text-sm flex items-center gap-1.5 mb-3"><ArrowLeft size={14} /> Back to console</Link>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2"><Bot size={22} className="text-brand" /><h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Growth Agent Work Log</h1></div>
          <a href="/growth-agent" target="_blank" rel="noopener noreferrer" className="text-sm text-slate-500 hover:text-brand px-2">Preview ↗</a>
        </div>
        <p className="text-sm text-slate-500 mt-2">Real agent activity plus anything your team records. <span className="text-emerald-600 font-semibold">{autoCount}</span> auto-recorded (e.g. completed audits); the rest are managed here.</p>

        {/* add */}
        <div className="bg-white border border-slate-200 rounded-2xl card-shadow p-5 mt-6" data-testid="worklog-add-form">
          <div className="text-sm font-semibold text-slate-800 mb-3">Record an action</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><span className={label}>Action</span><input value={form.action} onChange={(e) => set({ action: e.target.value })} placeholder="e.g. Proposed new homepage headline" className={input} data-testid="worklog-action" /></div>
            <div className="sm:col-span-2"><span className={label}>Reason</span><input value={form.reason} onChange={(e) => set({ reason: e.target.value })} placeholder="Why the agent did this" className={input} data-testid="worklog-reason" /></div>
            <div><span className={label}>Pages</span><input value={form.pages} onChange={(e) => set({ pages: e.target.value })} placeholder="/services, /about" className={input} data-testid="worklog-pages" /></div>
            <div><span className={label}>Mode</span><select value={form.mode} onChange={(e) => set({ mode: e.target.value })} className={input} data-testid="worklog-mode">{MODES.map((m) => <option key={m}>{m}</option>)}</select></div>
            <div><span className={label}>Before</span><input value={form.before} onChange={(e) => set({ before: e.target.value })} className={input} data-testid="worklog-before" /></div>
            <div><span className={label}>After</span><input value={form.after} onChange={(e) => set({ after: e.target.value })} className={input} data-testid="worklog-after" /></div>
            <div><span className={label}>Status</span><select value={form.status} onChange={(e) => set({ status: e.target.value })} className={input} data-testid="worklog-status">{STATUSES.map((s) => <option key={s}>{s}</option>)}</select></div>
            <label className="flex items-center gap-2 text-sm text-slate-600 mt-6"><input type="checkbox" checked={form.rollback} onChange={(e) => set({ rollback: e.target.checked })} /> Rollback available</label>
          </div>
          <button onClick={add} disabled={adding} data-testid="worklog-add-btn" className="mt-4 inline-flex items-center gap-1.5 bg-brand text-white rounded-lg px-5 py-2.5 text-sm font-semibold disabled:opacity-60">{adding ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Add entry</button>
        </div>

        {/* list */}
        <div className="space-y-2 mt-6" data-testid="worklog-list">
          {items.map((it) => (
            <div key={it.id} className="bg-white border border-slate-200 rounded-xl card-shadow p-4" data-testid={`worklog-item-${it.id}`}>
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-slate-800">{it.action}</span>
                    {it.auto && <span className="text-[10px] font-bold uppercase rounded-full px-2 py-0.5 bg-slate-900 text-white flex items-center gap-1"><Zap size={9} /> Auto</span>}
                    <span className={`text-[10px] font-bold uppercase rounded-full px-2 py-0.5 ${MODE_TONE[it.mode] || "bg-slate-100"}`}>{it.mode}</span>
                    <button onClick={() => cycleStatus(it)} data-testid={`worklog-status-${it.id}`} className={`text-[10px] font-bold uppercase rounded-full px-2 py-0.5 flex items-center gap-1 ${STATUS_TONE[it.status] || "bg-slate-100"}`} title="Click to change status">
                      {it.status === "done" ? <CheckCircle2 size={9} /> : <Clock size={9} />} {it.status}
                    </button>
                  </div>
                  {it.reason && <div className="text-[12px] text-slate-500 mt-1">{it.reason}</div>}
                  <div className="text-[11px] text-slate-400 mt-1">{it.time}{it.pages ? ` · ${it.pages}` : ""}</div>
                </div>
                <button onClick={() => del(it.id)} data-testid={`worklog-del-${it.id}`} className="text-slate-300 hover:text-red-500 p-1.5 shrink-0"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </main>
  );
}
